import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import powerDialerStore from "Stores/powerDialerStore";
import ToggleBtnType from "Components/common/ToggleBtnType";
import { toasterState } from "Components/common/toaster";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { startWavRecording } from "Utils/wavRecorder";

const MAX_SECONDS = 30;

const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const Toggle = ({ id, label, desc, value, onChange }) => (
  <div className="pdRow">
    <div>
      {label}
      {desc && <div className="pdDesc">{desc}</div>}
    </div>
    <ToggleBtnType id={id} defaultValue={!!value} cbk={onChange} />
  </div>
);

const Recordings = observer(() => {
  const { recordings } = powerDialerStore;
  const [recorder, setRecorder] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [playing, setPlaying] = useState("");
  const [pending, setPending] = useState(null);
  const [pendingName, setPendingName] = useState("");
  const audioRef = useRef(null);
  const fileRef = useRef(null);
  const secondsRef = useRef(0);

  const save = async (file, name, durationSec) => {
    const ok = await powerDialerStore.uploadRecording(file, name, durationSec);
    ok && toasterState.setToastMsg("Recording saved", "success");
  };

  const stopRecording = async () => {
    if (!recorder) return;
    setPending({ blob: recorder.stop(), seconds: secondsRef.current });
    setPendingName(`Recording ${recordings.length + 1}`);
    setRecorder(null);
  };

  const savePending = async () => {
    const name = pendingName.trim();
    if (!name) return;
    setPending(null);
    await save(pending.blob, name, pending.seconds);
  };

  const startRecording = async () => {
    try {
      const rec = await startWavRecording();
      secondsRef.current = 0;
      setSeconds(0);
      setRecorder(rec);
    } catch (e) {
      toasterState.setToastMsg("Microphone access is needed to record.", "failure");
    }
  };

  useEffect(() => {
    if (!recorder) return undefined;
    const t = setInterval(() => {
      secondsRef.current += 1;
      setSeconds(secondsRef.current);
      if (secondsRef.current >= MAX_SECONDS) stopRecording();
    }, 1000);
    return () => clearInterval(t);
  }, [recorder]);

  const onUpload = async e => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(mp3|wav)$/i.test(file.name)) {
      toasterState.setToastMsg("Use an MP3 or WAV file", "failure");
      return;
    }
    // 0 = length unknown (metadata error, or no event within 3s, e.g. in a background tab).
    const url = URL.createObjectURL(file);
    const duration = await new Promise(resolve => {
      const audio = new Audio(url);
      const timer = setTimeout(() => resolve(0), 3000);
      audio.onloadedmetadata = () => {
        clearTimeout(timer);
        resolve(audio.duration || 0);
      };
      audio.onerror = () => {
        clearTimeout(timer);
        resolve(0);
      };
    });
    URL.revokeObjectURL(url);
    const isWav = /\.wav$/i.test(file.name);
    if (!duration && !isWav) {
      toasterState.setToastMsg("Couldn't read the MP3's length. Try again, or upload a WAV.", "failure");
      return;
    }
    if (duration > MAX_SECONDS) {
      toasterState.setToastMsg("Keep it under 30 seconds", "failure");
      return;
    }
    // WAV with unknown length: the server reads it from the file header.
    await save(file, file.name.replace(/\.(mp3|wav)$/i, ""), duration ? Math.ceil(duration) : undefined);
  };

  const play = r => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing === r.id) {
      audio.pause();
      setPlaying("");
      return;
    }
    audio.src = r.url;
    audio.play();
    setPlaying(r.id);
  };

  const remove = r => {
    confirmationPopupState.setPopupValues({
      content: `Delete “${r.name}”?`,
      actionBtnText: "Delete",
      callback: async () => {
        confirmationPopupState.setShowPopup(false);
        await powerDialerStore.deleteRecording(r.id);
      }
    });
    confirmationPopupState.setShowPopup(true);
  };

  return (
    <div className="pdCard">
      <h3>My recordings</h3>
      <audio ref={audioRef} onEnded={() => setPlaying("")} />
      {!recordings.length && <div className="pdDesc">No recordings yet.</div>}
      {recordings.map(r => (
        <div className="pdRec" key={r.id}>
          <span className="pdPlay" onClick={() => play(r)}>
            <i className="material-icons">{playing === r.id ? "pause" : "play_arrow"}</i>
          </span>
          <span className="pdRecName">
            {r.name} <span className="pdDesc">· {fmt(r.durationSec || 0)}</span>
          </span>
          {r.isDefault ? (
            <span className="pdChip">Default</span>
          ) : (
            <span className="pdLink" onClick={() => powerDialerStore.setDefaultRecording(r.id)}>
              Set default
            </span>
          )}
          <i className="material-icons pdDelete" onClick={() => remove(r)}>
            delete_outline
          </i>
        </div>
      ))}
      {pending && (
        <div className="pdActions">
          <input
            className="pdNameInput"
            value={pendingName}
            autoFocus
            onChange={e => setPendingName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && savePending()}
          />
          <span className="pdBtn" onClick={savePending}>
            Save
          </span>
          <span className="pdBtn" onClick={() => setPending(null)}>
            Discard
          </span>
        </div>
      )}
      <div className="pdActions">
        {recorder ? (
          <span className="pdBtn recording" onClick={stopRecording}>
            <i className="material-icons">stop</i>Stop · {fmt(seconds)}
          </span>
        ) : (
          <span className="pdBtn" onClick={startRecording}>
            <i className="material-icons">mic</i>Record new
          </span>
        )}
        <span className="pdBtn" onClick={() => fileRef.current?.click()}>
          <i className="material-symbols-outlined">upload</i>Upload
        </span>
        <input ref={fileRef} type="file" accept=".mp3,.wav,audio/mpeg,audio/wav" hidden onChange={onUpload} />
      </div>
      <div className="pdDesc">Keep it under 30 seconds. MP3 or WAV. Each rep has their own recordings.</div>
    </div>
  );
});

const Voicemail = observer(() => {
  const { settings, saveSettings, loadSettings } = powerDialerStore;

  useEffect(() => {
    loadSettings();
  }, []);

  return (
    <div className="pdSettings">
      <div className="pdCard">
        <h3>Voicemail drop</h3>
        <Toggle
          id="detectVoicemail"
          label="Detect voicemail"
          desc="Knows when a call wasn’t picked up and went to voicemail. A live person hears a short pause before connecting."
          value={settings.detectVoicemail}
          onChange={v => saveSettings({ detectVoicemail: v })}
        />
        <div className="pdRow">
          <div>
            When a call reaches voicemail
            <div className="pdDesc">You get a Drop voicemail button, in click-to-call and the power dialer</div>
          </div>
        </div>
        <Toggle
          id="autoVoicemailDrop"
          label="Auto voicemail drop"
          desc="Drops your default message without asking"
          value={settings.autoVoicemailDrop}
          onChange={v => saveSettings({ autoVoicemailDrop: v })}
        />
        <Toggle
          id="includeMobileInAutoDrop"
          label="Include mobile numbers in auto drop"
          desc="Prerecorded messages to US mobiles need prior consent"
          value={settings.includeMobileInAutoDrop}
          onChange={v => saveSettings({ includeMobileInAutoDrop: v })}
        />
        <div className="pdRow">
          <div>
            Logged as
            <div className="pdDesc">
              Dropped: Left voicemail, note “Voicemail dropped” or “Auto voicemail dropped”. Skipped: Voicemail, none left
            </div>
          </div>
        </div>
      </div>
      <Recordings />
    </div>
  );
});

export default Voicemail;
